import { ApiError } from '@/domains/api';
import type { Can, Permission } from '@/domains/auth';
import { participantsApi } from './api/participants-api';
import { secAdvApi } from './api/sec-adv-api';
import { TEAM_PATHS } from './paths';
import { refusalMessage } from './refusal';

/**
 * The four lists one search reaches (SR-414, record search D1; the plan's Q1, D19–D21) — People,
 * Teams, Firms and the SEC ADV register, in that order, each under its own list's guard. The top
 * search (Ctrl/⌘+K) asks each for its first five rows and its total; the strip under each list's
 * search box (`SearchScopes`) asks each for its total alone. Both say the same lists in the same
 * words and send a reader to the same address, so they read them here.
 *
 * **A list is searched exactly as its own page searches it**, with `q` alone: People with
 * `include=roster`, which its page derives from a non-empty `q` (the owner's Q3) — so a group's
 * five rows are that list's first page, and its total that page's "N found" (the design's D1).
 * Nothing here counts, ranks or filters: every total is survey-api's.
 */

export type SearchScope = 'people' | 'teams' | 'firms' | 'register';

/** The order the palette's groups, the strip's links and Enter's choice all follow. */
export const SEARCH_SCOPES: readonly SearchScope[] = ['people', 'teams', 'firms', 'register'];

/** Each list's name, as its rail entry and its page call it. */
export const SCOPE_LABEL: Readonly<Record<SearchScope, string>> = {
  people: 'People',
  teams: 'Teams',
  firms: 'Firms',
  register: 'SEC ADV register'
};

/** What one row of each list is, one and many — "See all 23 people". */
export const SCOPE_NOUNS: Readonly<Record<SearchScope, [string, string]>> = {
  people: ['person', 'people'],
  teams: ['team', 'teams'],
  firms: ['firm', 'firms'],
  register: ['register firm', 'register firms']
};

/** The permission each list's guard names — survey-api's policy on its read. */
const SCOPE_PERMISSION: Readonly<Record<SearchScope, Permission>> = {
  people: 'responses.browse',
  teams: 'responses.browse',
  firms: 'responses.browse',
  register: 'registry.browse'
};

/** The lists this account opens, in order — the only ones searched or linked. */
export function searchedScopes(can: Can): SearchScope[] {
  return SEARCH_SCOPES.filter((scope) => can(SCOPE_PERMISSION[scope]));
}

/** Each list's own page. */
export const SCOPE_PATH: Readonly<Record<SearchScope, string>> = {
  people: TEAM_PATHS.people,
  teams: TEAM_PATHS.teams,
  firms: TEAM_PATHS.firms,
  register: TEAM_PATHS.secAdvFirms
};

/** A list under the search `q` alone — what "See all" and the strip's links open. */
export function scopeListPath(scope: SearchScope, q: string): string {
  return `${SCOPE_PATH[scope]}?${new URLSearchParams({ q }).toString()}`;
}

/** The search results page under `q` — every list's matches on one page (SR-463). */
export function searchPath(q: string): string {
  return `${TEAM_PATHS.search}?${new URLSearchParams({ q }).toString()}`;
}

/** "See all 23 people" — a group's way to its list. */
export function seeAllWords(scope: SearchScope, total: number): string {
  const [one, many] = SCOPE_NOUNS[scope];
  return total === 1 ? `See 1 ${one}` : `See all ${total} ${many}`;
}

/**
 * A list's page as a search read it — **a 2xx without `items` and a numeric `total` is a failed
 * read**, never an empty result: an empty body (`null`), `index.html` from a misrouted base URL,
 * or a page with no total would otherwise be drawn as "nobody matches" or counted as zero, which
 * is the client's SR-386 again.
 */
export function searchedPage<T extends { items: unknown[]; total: number }>(page: T): T {
  const shaped = page as Partial<T> | null;
  if (!Array.isArray(shaped?.items) || typeof shaped?.total !== 'number') {
    throw new Error('The answer was not a list');
  }
  return page;
}

/**
 * What a failed search says: a refusal (a status under 500) in survey-api's words — a ninth
 * word's 400 by its `problems[]` — and anything else as an answer that never arrived, never in
 * the browser's own words ("Failed to fetch").
 */
export function searchFailure(error: unknown, label: string): string {
  return error instanceof ApiError && error.status < 500
    ? refusalMessage(error, `${label} could not be searched`)
    : `${label} could not be searched — no answer came back. Try again.`;
}

/** A list's total under `q` alone: one row asked, the page's `total` read. */
export async function countScope(
  scope: SearchScope,
  q: string,
  signal?: AbortSignal
): Promise<number> {
  const query = { q, pageSize: 1 };
  switch (scope) {
    case 'people':
      return searchedPage(
        await participantsApi.listPeople({ ...query, include: 'roster' }, signal)
      ).total;
    case 'teams':
      return searchedPage(await participantsApi.listTeams(query, signal)).total;
    case 'firms':
      return searchedPage(await participantsApi.listFirms(query, signal)).total;
    case 'register':
      return searchedPage(await secAdvApi.listFirms(query, signal)).total;
  }
}
